/**
 * Sistema de permissões baseado em roles
 * Utiliza as definições de roles.js para verificar permissões
 */

import { ROLES, hasPermission, canAccess } from "../constants/roles";
import FEATURE_FLAGS from "../config/featureFlags";
import { roleHasBasePermission } from "./roleBasePermissions";

/**
 * Verifica se o usuário tem uma permissão específica
 * @param {string} userRole - Role do usuário
 * @param {string} permission - Permissão a verificar
 * @returns {boolean}
 */
export const checkPermission = (
  userRole,
  permission,
  effectivePermissions = null,
  options = {}
) => {
  const { granularEnabled = true, permissionsLoaded = false } = options;

  if (FEATURE_FLAGS.granularPermissions && permission.includes(".")) {
    if (Array.isArray(effectivePermissions) && effectivePermissions.includes(permission)) {
      return true;
    }
    // Permissões já carregadas: confia na lista efetiva (revokes granulares prevalecem).
    if (granularEnabled && permissionsLoaded) {
      return false;
    }
    // Fallback alinhado ao role base enquanto cache/API não refletiu novas permissões.
    return roleHasBasePermission(userRole, permission);
  }
  return hasPermission(userRole, permission);
};

/**
 * Verifica permissão granular efetiva para ações mutáveis (criar/editar/excluir).
 * Não usa fallback de role base quando permissões já foram carregadas.
 */
export const checkEffectivePermission = (
  userRole,
  permission,
  effectivePermissions = null,
  options = {}
) => {
  const { granularEnabled = true, permissionsLoaded = false } = options;

  if (!FEATURE_FLAGS.granularPermissions || !permission.includes(".")) {
    return hasPermission(userRole, permission);
  }
  if (Array.isArray(effectivePermissions) && effectivePermissions.includes(permission)) {
    return true;
  }
  if (granularEnabled && permissionsLoaded) {
    return false;
  }
  // Enquanto carrega: ocultar ações por padrão (fail-closed).
  return false;
};

/**
 * Verifica se o usuário tem acesso a múltiplas permissões
 * @param {string} userRole - Role do usuário
 * @param {...string} permissions - Permissões a verificar
 * @returns {boolean}
 */
export const checkAccess = (userRole, permissions = [], effectivePermissions = null) => {
  if (!Array.isArray(permissions)) {
    return false;
  }
  if (FEATURE_FLAGS.granularPermissions && permissions.every(p => p.includes("."))) {
    return permissions.every(permission => checkPermission(userRole, permission, effectivePermissions));
  }
  return canAccess(userRole, ...permissions);
};

/**
 * Verifica se o usuário é admin
 * @param {string} userRole - Role do usuário
 * @returns {boolean}
 */
export const isAdmin = userRole => {
  return userRole === ROLES.ADMIN;
};

/**
 * Verifica se o usuário pode gerenciar usuários
 * @param {string} userRole - Role do usuário
 * @returns {boolean}
 */
export const canManageUsers = (userRole, effectivePermissions = null) => {
  if (FEATURE_FLAGS.granularPermissions) {
    return checkPermission(userRole, "usuarios.usuario.visualizar", effectivePermissions);
  }
  return checkPermission(userRole, "canManageUsers", effectivePermissions);
};

/**
 * Verifica se o usuário pode gerenciar alunos
 * @param {string} userRole - Role do usuário
 * @returns {boolean}
 */
export const canManageAlunos = (userRole, effectivePermissions = null) => {
  if (FEATURE_FLAGS.granularPermissions) {
    return checkPermission(userRole, "alunos.aluno.visualizar", effectivePermissions);
  }
  return checkPermission(userRole, "canManageAlunos", effectivePermissions);
};

/**
 * Verifica se o usuário pode registrar frequência
 * @param {string} userRole - Role do usuário
 * @returns {boolean}
 */
export const canRegisterFrequencia = (userRole, effectivePermissions = null) => {
  if (FEATURE_FLAGS.granularPermissions) {
    return checkPermission(userRole, "frequencia.lancamento.criar", effectivePermissions);
  }
  return checkPermission(userRole, "canRegisterFrequencia", effectivePermissions);
};

/**
 * Verifica se o usuário pode gerenciar avaliações
 * Para MONITOR, sempre permite acesso à aba (a validação de criação é feita em outro momento)
 * ADMIN / ADMINISTRATIVO / TECNICO / TECNICO_GERAL sempre podem visualizar
 * Para outros roles, usa a verificação padrão de roles / permissões granulares
 * @param {string} userRole - Role do usuário
 * @param {boolean} hasPedagogicActivity - Se o monitor está vinculado a atividade pedagógica (opcional, para MONITOR)
 * @returns {boolean}
 */
export const canManageAvaliacoes = (userRole, hasPedagogicActivity = null, effectivePermissions = null) => {
  // Para MONITOR, sempre permite acesso à aba de avaliações
  // A validação de criação de avaliações é feita no momento de criar, não aqui
  if (userRole === "MONITOR") {
    return true;
  }
  // Perfis administrativos sempre visualizam avaliações (não dependem de vínculo como monitor)
  if (["ADMIN", "ADMINISTRATIVO", "TECNICO", "TECNICO_GERAL"].includes(userRole)) {
    return true;
  }
  if (FEATURE_FLAGS.granularPermissions) {
    return checkPermission(userRole, "avaliacoes.avaliacao.visualizar", effectivePermissions);
  }
  // Para outros roles, usa a verificação padrão
  return checkPermission(userRole, "canManageAvaliacoes", effectivePermissions);
};

/**
 * Verifica se o usuário pode visualizar dashboards
 * @param {string} userRole - Role do usuário
 * @returns {boolean}
 */
export const canViewDashboards = (userRole, effectivePermissions = null) => {
  if (FEATURE_FLAGS.granularPermissions) {
    return checkPermission(userRole, "dashboard.resumo.visualizar", effectivePermissions);
  }
  return checkPermission(userRole, "canViewDashboards", effectivePermissions);
};

/**
 * Verifica se o usuário pode gerar relatórios
 * @param {string} userRole - Role do usuário
 * @returns {boolean}
 */
export const canGenerateReports = (userRole, effectivePermissions = null) => {
  if (FEATURE_FLAGS.granularPermissions) {
    return checkPermission(userRole, "relatorios.relatorio.visualizar", effectivePermissions);
  }
  return checkPermission(userRole, "canGenerateReports", effectivePermissions);
};

/**
 * Verifica se o usuário pode gerenciar planejamentos
 * @param {string} userRole - Role do usuário
 * @returns {boolean}
 */
export const canManagePlanejamentos = (userRole, effectivePermissions = null) => {
  if (FEATURE_FLAGS.granularPermissions) {
    return checkPermission(userRole, "planejamentos.planejamento.visualizar", effectivePermissions);
  }
  return ["ADMIN", "ADMINISTRATIVO", "GESTOR", "SECRETARIA", "COORDENADOR", "MONITOR"].includes(userRole);
};

/**
 * Painel de administração granular é exclusivo do perfil TECNICO.
 */
export const canAccessPermissionsAdminPanel = userRole => {
  return userRole === "TECNICO";
};

/**
 * @deprecated Não usado no fluxo de lançamento de frequência retroativa.
 * O limite retroativo (padrão 1 dia, extensível por exceção por usuário) é
 * resolvido inteiramente no backend (função SQL registrar_frequencia) e
 * consultado no frontend via hook useLimiteRetroativo — não por esta função
 * nem pela permissão "frequencia.retroativa.registrar".
 * Ver docs/FREQUENCIA_RETROATIVA_POR_USUARIO.md. Mantida apenas para não
 * quebrar quem eventualmente já importe esta função; não usar em código novo.
 */
export const canRegistrarRetroativo = (userRole, effectivePermissions = null) => {
  if (FEATURE_FLAGS.granularPermissions) {
    return checkPermission(userRole, "frequencia.retroativa.registrar", effectivePermissions);
  }
  return ["ADMIN", "ADMINISTRATIVO", "TECNICO", "TECNICO_GERAL"].includes(userRole);
};

export default {
  checkPermission,
  checkEffectivePermission,
  checkAccess,
  isAdmin,
  canManageUsers,
  canManageAlunos,
  canRegisterFrequencia,
  canManageAvaliacoes,
  canViewDashboards,
  canGenerateReports,
  canManagePlanejamentos,
  canAccessPermissionsAdminPanel,
  canRegistrarRetroativo,
};
